import {
	afterNextRender,
	afterRenderEffect,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	input,
	signal,
	viewChild,
	viewChildren
} from '@angular/core';
import { UiButtonComponent, UIIconComponent, UiDividerComponent, UiNumberComponent } from '@design-system';
import { MASKED_VALUE, PortfolioViewStateService } from '../../data-access/portfolio-view-state.service';
import { IconNames } from '@icon/assets';

/** Segments in the carousel's position indicator, per the handoff's Slider. */
const INDICATOR_SEGMENTS = 2;

/** One of the three info cards the handoff allows. */
interface SummaryCard {
	label: string;
	value: number;
	/** Rendered before the value; only the portfolio profit/loss card has one. */
	percent: string | null;
	/** Null keeps the figure neutral; true/false colors it as a gain/loss. */
	isProfit: boolean | null;
	dataCy: string;
	percentDataCy: string;
}

/**
 * Aggregate state of the kala portfolio: total assets, plus the three info
 * cards the handoff annotation pins down — "در بورس کالا تنها سه تا کارت:
 * ارزش پرتفوی، سود و زیان پرتفوی، سود و زیان امروز رو داریم".
 *
 * The caller only renders this when at least one holding exists, so the
 * component never has to represent an empty portfolio.
 */
@Component({
	selector: 'app-portfolio-summary',
	imports: [UiButtonComponent, UIIconComponent, UiDividerComponent, UiNumberComponent],
	templateUrl: './portfolio-summary.component.html',
	host: {
		class: 'block w-full'
	}
})
export class PortfolioSummaryComponent {
	/** دارایی کل بورس‌کالا — portfolio value plus uninvested cash. */
	readonly totalAssets = input.required<number>();
	/** ارزش کل پرتفوی */
	readonly totalNetSaleValue = input.required<number>();
	/** سود و زیان پرتفوی */
	readonly totalProfitLoss = input.required<number>();
	readonly totalProfitLossPercent = input.required<number>();
	/** سود و زیان امروز */
	readonly todayProfitLoss = input.required<number>();

	private readonly viewState = inject(PortfolioViewStateService);

	private readonly carousel = viewChild<ElementRef<HTMLElement>>('carousel');
	// Named `cardEl` in the template: a `#card` ref would collide with the `@for`
	// loop variable and the query would silently match nothing.
	private readonly cardElements = viewChildren<ElementRef<HTMLElement>>('cardEl');

	/**
	 * The eye toggle, held in `PortfolioViewStateService` rather than here: it is a
	 * stored user setting, and it also masks the two figures on each asset card,
	 * which this header does not own.
	 */
	readonly amountsHidden = this.viewState.amountsHidden;

	/**
	 * Card widths captured at the moment of hiding, in card order. Held in the
	 * view state beside `amountsHidden`, which outlives this component — a local
	 * copy would come back empty on return from the detail screen and collapse
	 * cards the persisted flag still says are frozen.
	 */
	private readonly frozenCardWidths = this.viewState.frozenCardWidths;

	/** True only while the row actually overflows, so the indicator matches reality. */
	private readonly isScrollable = signal(false);
	/** How far through the overflow the row is scrolled, 0 to 1. */
	private readonly scrollProgress = signal(0);

	readonly visibilityIcon = computed<IconNames>(() => (this.amountsHidden() ? 'eye-off' : 'eye'));
	readonly visibilityLabel = computed(() => (this.amountsHidden() ? 'نمایش مبالغ' : 'پنهان کردن مبالغ'));

	readonly isTotalProfit = computed(() => this.totalProfitLoss() >= 0);
	readonly isTodayProfit = computed(() => this.todayProfitLoss() >= 0);

	readonly formattedTotalProfitLossPercent = computed(() => `(${this.totalProfitLossPercent().toFixed(2)}%)`);

	readonly maskedValue = MASKED_VALUE;

	readonly cards = computed<SummaryCard[]>(() => [
		{
			label: 'ارزش پرتفوی',
			value: this.totalNetSaleValue(),
			percent: null,
			isProfit: null,
			dataCy: 'portfolio-summary-value',
			percentDataCy: 'portfolio-summary-value-percent'
		},
		{
			label: 'سود و زیان پرتفوی',
			value: this.totalProfitLoss(),
			percent: this.formattedTotalProfitLossPercent(),
			isProfit: this.isTotalProfit(),
			dataCy: 'portfolio-summary-profit-loss',
			percentDataCy: 'portfolio-summary-profit-loss-percent'
		},
		{
			label: 'سود و زیان امروز',
			value: this.todayProfitLoss(),
			percent: null,
			isProfit: this.isTodayProfit(),
			dataCy: 'portfolio-summary-today-profit-loss',
			percentDataCy: 'portfolio-summary-today-profit-loss-percent'
		}
	]);

	/** Per-card frozen width, or null per card while the amounts are visible. */
	readonly cardWidths = computed<(number | null)[]>(() => {
		const frozen = this.frozenCardWidths();
		return this.cards().map((_, index) => (this.amountsHidden() ? (frozen[index] ?? null) : null));
	});

	/** Empty while the row fits, so the template renders no indicator at all. */
	readonly indicatorSegments = computed<number[]>(() =>
		this.isScrollable() ? Array.from({ length: INDICATOR_SEGMENTS }, (_, index) => index) : []
	);

	/**
	 * The lit segment. Derived from how far the row is scrolled rather than from a
	 * page index: the cards overflow by well under one viewport, so a
	 * `scrollLeft / clientWidth` page number could never leave 0.
	 */
	readonly activeSegment = computed(() =>
		Math.min(INDICATOR_SEGMENTS - 1, Math.floor(this.scrollProgress() * INDICATOR_SEGMENTS))
	);

	constructor() {
		const destroyRef = inject(DestroyRef);

		// Re-measure whenever the figures or the mask change. A ResizeObserver on the
		// row never sees this: the row's own box is fixed by the layout, and only its
		// scrollWidth moves when the numbers get wider or narrower. This also covers
		// the first render, so the indicator is never missing until the first swipe.
		afterRenderEffect(() => {
			this.cards();
			this.amountsHidden();
			this.measureCarousel();
		});

		afterNextRender(() => {
			// A ResizeObserver keeps the row right across rotation and split-screen.
			const element = this.carousel()?.nativeElement;
			if (!element) {
				return;
			}

			const observer = new ResizeObserver(() => this.measureCarousel());
			observer.observe(element);
			destroyRef.onDestroy(() => observer.disconnect());
		});
	}

	onCarouselScroll(): void {
		this.measureCarousel();
	}

	/** Scrolls the row to the point where `segment` becomes the lit one. */
	scrollToSegment(segment: number): void {
		const element = this.carousel()?.nativeElement;
		if (!element) {
			return;
		}

		const overflow = element.scrollWidth - element.clientWidth;
		const progress = INDICATOR_SEGMENTS > 1 ? segment / (INDICATOR_SEGMENTS - 1) : 0;
		// Spec RTL scrollLeft runs from 0 toward negative values, which is what every
		// current engine implements; the scroll handler then re-measures as it moves.
		const direction = getComputedStyle(element).direction === 'rtl' ? -1 : 1;
		element.scrollTo({ left: direction * overflow * progress, behavior: 'smooth' });
	}

	toggleAmounts(): void {
		if (this.amountsHidden()) {
			// The widths are deliberately left standing rather than cleared here.
			// `cardWidths` already gates them on `amountsHidden`, so they are inert
			// once the figures are visible — and the save can still fail, which would
			// leave the cards masked. Clearing first would collapse them in exactly
			// the case the freeze exists to prevent. The next hide overwrites them.
			this.viewState.setAmountsHidden(false);
			return;
		}

		// Measure before the figures are replaced, so the widths recorded are the
		// ones the real content produced. The save is awaited by nobody: the mask
		// follows the stored setting, so a rejected write simply leaves the figures
		// visible and these widths unused.
		const widths = this.cardElements().map(card => card.nativeElement.offsetWidth);
		// A zero measurement means the row was not laid out — hidden, detached, or
		// measured before first paint. Freezing zeros would collapse every card to
		// nothing, so in that case the cards stay content-sized.
		const measured = widths.length > 0 && widths.every(width => width > 0);
		this.viewState.setFrozenCardWidths(measured ? widths : []);
		this.viewState.setAmountsHidden(true);
	}

	private measureCarousel(): void {
		const element = this.carousel()?.nativeElement;
		if (!element) {
			return;
		}

		const overflow = element.scrollWidth - element.clientWidth;
		this.isScrollable.set(overflow > 0);
		// RTL scrollLeft counts away from the right edge, and browsers disagree on
		// its sign, so the magnitude is what measures progress.
		this.scrollProgress.set(overflow > 0 ? Math.min(1, Math.abs(element.scrollLeft) / overflow) : 0);
	}
}
